import React from "react";
import { Modal, Box, Flex, Text, Loader, createStyles } from "@mantine/core";
import { QR, BTN_RESET } from "../theme";

const useStyles = createStyles(() => ({
  body: {
    fontFamily: "Inter",
    fontSize: 13.5,
    lineHeight: 1.55,
    color: QR.muted,
  },
  btnGhost: {
    ...BTN_RESET,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    color: QR.ink2,
    fontWeight: 600,
    fontSize: 13.5,
    fontFamily: "Inter",
    textAlign: "center",
    "&:hover": { background: QR.surfaceSoft },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },
  btnConfirm: {
    ...BTN_RESET,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    border: "1px solid transparent",
    fontWeight: 700,
    fontSize: 13.5,
    fontFamily: "Inter",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    gap: 8,
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },
  danger: {
    backgroundColor: QR.danger,
    color: "#FFF",
    "&:hover": { backgroundColor: QR.dangerDeep, color: "#FFF" },
  },
  primary: {
    backgroundColor: QR.accent,
    color: QR.ink,
    "&:hover": { backgroundColor: QR.accentHover, color: QR.ink },
  },
}));

/**
 * Mantine replacement for window.confirm — the native dialog can't be styled,
 * blocks the whole tab, and is suppressible by the browser.
 *
 * Unlike window.confirm this is async: render it with `opened` driven by the
 * pending item and run the action from `onConfirm`.
 */
const ConfirmModal = ({
  opened,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busyLabel,
  busy = false,
  danger = false,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Modal
      opened={opened}
      // A destructive action shouldn't be dismissable by a stray click or a
      // keystroke aimed at the page behind it — Cancel is always right there.
      onClose={busy ? () => {} : onClose}
      title={title}
      radius="md"
      size="sm"
      centered
      zIndex={1200}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
    >
      {message && (
        <Box className={classes.body}>
          {typeof message === "string" ? <Text className={classes.body}>{message}</Text> : message}
        </Box>
      )}
      <Flex justify="flex-end" gap={10} mt={22}>
        <button type="button" className={classes.btnGhost} onClick={onClose} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={cx(classes.btnConfirm, danger ? classes.danger : classes.primary)}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy && <Loader size={13} color="currentColor" />}
          {busy ? busyLabel || confirmLabel : confirmLabel}
        </button>
      </Flex>
    </Modal>
  );
};

export default ConfirmModal;
